import React, { useState } from 'react';
import { X, CreditCard, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';

const InputField = ({ label, icon: Icon, ...props }) => (
  <div className="space-y-1">
    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest px-1">
      {label}
    </label>
    <div className="bg-white/5 border border-white/10 rounded-2xl flex items-center px-4 focus-within:border-purple-500 transition-all">
      {Icon && <Icon size={18} className="text-gray-500 mr-2" />}
      <input
        {...props}
        className="bg-transparent outline-none flex-1 py-4 text-base font-bold text-white w-full"
      />
    </div>
  </div>
);

const PaymentModal = ({ session, onSave, onCancel }) => {
  const [data, setData] = useState({
    method: 'cash',
    cardRef: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Helper function to format local time safely to MySQL DATETIME format (YYYY-MM-DD HH:MM:SS)
  const formatLocalToMySQL = (dateObj) => {
    const pad = (num) => String(num).padStart(2, '0');
    
    const year = dateObj.getFullYear();
    const month = pad(dateObj.getMonth() + 1);
    const day = pad(dateObj.getDate());
    const hours = pad(dateObj.getHours());
    const minutes = pad(dateObj.getMinutes());
    const seconds = pad(dateObj.getSeconds());
    
    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  };

  if (!session) return null;

  const handleComplete = async () => {
    // Validation
    if (data.method === 'card' && !data.cardRef) return setError('Card Reference required');
    
    if (!showConfirm) {
      setShowConfirm(true);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://companylocker.com/admin/wp-json/dpt/v1/end', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: session.id,
          actual_end: formatLocalToMySQL(new Date()),
          ref_number: data.cardRef || '',
          type: data.method,
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        onSave(data);
      } else {
        setError(result.message || 'Failed to update session on server');
        setShowConfirm(false);
      }
    } catch (err) {
      setError('Network error. Check your connection.');
      setShowConfirm(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#05050f]/98 backdrop-blur-2xl z-[100] flex flex-col p-6">
      <div className="max-w-md mx-auto w-full pt-4 h-full flex flex-col">
        <div className="flex justify-between items-center mb-8">
          <div className="space-y-1">
             <h2 className="text-2xl font-black italic tracking-tighter text-white">FINALIZE</h2>
             <p className="text-[10px] text-purple-400 font-bold uppercase">
               {session.dancer} • {session.room}
             </p>
          </div>
          <button 
            onClick={onCancel} 
            className="p-3 bg-white/5 rounded-full text-white" 
            disabled={loading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Payment Method Tab */}
        <div className="flex bg-white/5 p-1 rounded-2xl mb-8 border border-white/10">
          {['cash', 'card'].map((mode) => (
            <button
              key={mode}
              disabled={loading || showConfirm}
              onClick={() => setData({ ...data, method: mode })}
              className={`flex-1 py-3.5 rounded-xl text-[10px] font-black transition-all uppercase ${
                data.method === mode ? 'bg-white text-black shadow-lg' : 'text-gray-500'
              }`}
            >
              {mode}
            </button>
          ))}
        </div>

        <div className="space-y-5 flex-1 overflow-y-auto no-scrollbar pb-6">
          {error && (
            <div className="text-red-500 text-[10px] font-black uppercase flex items-center gap-2 bg-red-500/10 p-4 rounded-2xl border border-red-500/20">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          

          {data.method === 'card' && (
            <InputField
              label="Card Reference"
              icon={CreditCard}
              type="text"
              placeholder="AUTH CODE / REF"
              value={data.cardRef}
              onChange={(e) => {
                setData({ ...data, cardRef: e.target.value });
                setShowConfirm(false);
              }}
              disabled={loading || showConfirm}
            />
          )}

          {showConfirm && !loading && (
            <div className="bg-purple-600/10 border border-purple-500/30 p-5 rounded-3xl animate-in fade-in zoom-in duration-300">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-purple-500 shrink-0" size={18} />
                <div className="space-y-1">
                  <p className="text-white text-xs font-black uppercase tracking-tight">Verify Session End</p>
                  <p className="text-gray-400 text-[10px] leading-relaxed font-bold uppercase">
                    Confirming will close the room for {session.dancer}.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {showConfirm && !loading && (
             <button
             onClick={() => setShowConfirm(false)}
             className="w-full py-4 rounded-2xl font-black text-[10px] tracking-widest text-gray-500 uppercase border border-white/5"
           >
             Go Back
           </button>
          )}

          <button
            onClick={handleComplete}
            disabled={loading}
            className={`w-full py-5 rounded-2xl font-black text-xs tracking-widest shadow-lg active:scale-95 transition-all uppercase flex justify-center items-center text-white ${
              showConfirm 
              ? 'bg-green-600 shadow-green-500/20' 
              : 'bg-gradient-to-r from-purple-600 to-pink-600 shadow-purple-500/20'
            }`}
          >
            {loading ? (
              <Loader2 className="animate-spin" size={18} />
            ) : showConfirm ? (
              'YES, CLOSE SESSION'
            ) : (
              'Complete Session'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentModal;